import Keycloak from 'keycloak-js'
import { noop } from 'lodash'
import Config from '@/config'
import { setAuthorization, removeAuthorization } from '@/api'

let keycloak = null

function applyAuthHeader(kc) {
  if (!kc?.token) return
  // Always Bearer for OAuth2/OIDC access tokens
  setAuthorization({ type: 'Bearer', token: kc.token })
}

const signout = ({ storage } = {}) => {
  if (storage) storage.clean()
  removeAuthorization()
  if (keycloak) {
    // Optionally pass redirectUri if you need to come back to your app
    keycloak.logout()
  }
}

const refreshToken = async (minValidity = 70) => {
  if (!keycloak) return noop()
  if (!keycloak.authenticated) {
    return { ok: false, data: null }
  }

  try {
    const refreshed = await keycloak.updateToken(minValidity)
    if (refreshed) console.warn('Token was successfully refreshed')
    else console.warn('Token is still valid')
    applyAuthHeader(keycloak)
    return { ok: true, data: keycloak }
  } catch (err) {
    console.warn('Failed to refresh the token, or the session has expired')
    return { ok: false, data: null, error: err }
  }
}

const signin = async () =>
  new Promise((resolve, reject) => {
    const instance = new Keycloak(Config.keycloak)
    keycloak = instance

    instance
      .init({
        onLoad: 'check-sso',

        // v26: explicitly use Authorization Code Flow (recommended)
        flow: 'standard',
        pkceMethod: 'S256',

        // Leaving this false is OK, but you lose “logout in another tab” detection.
        checkLoginIframe: false,
      })
      .then(async (authenticated) => {
        try {
          if (!authenticated) {
            await instance.login()
            return
          }

          // If token is near expiry, refresh, otherwise just set header.
          if (instance.isTokenExpired()) {
            const refreshResult = await refreshToken()
            if (!refreshResult?.ok) {
              reject(refreshResult?.error || new Error('Unable to refresh token during signin'))
              return
            }
          } else {
            applyAuthHeader(instance)
          }

          if (Config.isDev) {
            instance.onAuthError = (data) => console.log('onAuthError', data)
            instance.onAuthRefreshError = (data) => console.log('onAuthRefreshError', data)
            instance.onTokenExpired = () => console.log('onTokenExpired')
            instance.onAuthLogout = () => console.log('onAuthLogout')
          }

          resolve({ ok: authenticated, data: instance })
        } catch (e) {
          reject(e)
        }
      })
      .catch(reject)
  })

const instance = () => keycloak

const user = () => {
  const tokenParsed = keycloak?.tokenParsed
  if (!tokenParsed) return undefined

  const { name: fullName, given_name: name, family_name: surname, email } = tokenParsed
  return { fullName, name, surname, email }
}

const accountManagement = () => {
  if (keycloak) keycloak.accountManagement()
}

export default {
  signin,
  signout,
  refreshToken,
  instance,
  user,
  accountManagement,
}